'use strict';

// external imports
import omniclone from 'omniclone';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';
import {encode as encodeHtmlEntities} from 'html-entities';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import Status from '~/shared/src/mm/Status';
import ItemType from '~/shared/src/mm/ItemType';
import Property, {
    PropertyAttributes,
    PropertyJsonAttributes,
    PropertyAttributesForCreation,
    PropertyAttributesForUpdate,
    PropertyAttributesSettable,
    isPropertyAttributes,
    isPropertyJsonAttributes
} from '~/shared/src/ci/Property';
import EnumProperty, {
    EnumPropertyAttributes,
    EnumPropertyJsonAttributes,
    EnumPropertyAttributesForCreation,
    EnumPropertyAttributesForUpdate,
    EnumPropertyAttributesSettable,
    isEnumPropertyAttributes,
    isEnumPropertyJsonAttributes
} from '~/shared/src/ci/EnumProperty';
import Reference from '~/shared/src/ci/Reference';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {
    convertToDate,
    convertValueToString
} from '~/shared/src/utilities/valueConverters';
import {
    ItemMetaDataMapping,
    PropertyMetaDataMapping
} from '~/shared/src/utilities/modules/data';
import StackError from '~/shared/src/base/StackError';

// backend imports
///////////////////////////
////////////////////////////////////////////////////
//////////


// type definitions
export type ItemAttributes = {
    id? : number;
    key? : string;
    name? : string;
    itemTypeId : number;
    statusId? : number;
    modifierUserId? : string;
    modificationTime? : string | NDate;
    creatorUserId? : string;
    creationTime? : string | NDate;
    validatorUserId? : string;
    validationTime? : string | NDate;
    validFromTime? : string | NDate;
    validToTime? : string | NDate;
    properties? : ( Omit<PropertyAttributes, 'itemId'> & Partial<Pick<PropertyAttributes, 'itemId'>> )[];
    enumProperties? : ( Omit<EnumPropertyAttributes, 'itemId'> & Partial<Pick<EnumPropertyAttributes, 'itemId'>> )[];
};
export const itemAttributes = keys<ItemAttributes>();
export const isItemAttributes = ( itemAttributesToTest : any ) : itemAttributesToTest is ItemAttributes => {
    let retVal = false;

    try {
        if ( typeof itemAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( itemAttributesToTest.id !== undefined && ( typeof itemAttributesToTest.id !== 'number' || itemAttributesToTest.is < 1 ) ) {
            throw new Error();
        }
        if ( itemAttributesToTest.key !== undefined && typeof itemAttributesToTest.key !== 'string' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.name !== undefined && typeof itemAttributesToTest.name !== 'string' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.itemTypeId === undefined || typeof itemAttributesToTest.itemTypeId !== 'number' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.statusId !== undefined && typeof itemAttributesToTest.statusId !== 'number' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.modifierUserId !== undefined && typeof itemAttributesToTest.modifierUserId !== 'string' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.modificationTime !== undefined && typeof itemAttributesToTest.modificationTime !== 'string' && !( itemAttributesToTest.modificationTime instanceof Date ) && itemAttributesToTest.modificationTime !== 'null' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.creatorUserId !== undefined && typeof itemAttributesToTest.creatorUserId !== 'string' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.creationTime !== undefined && typeof itemAttributesToTest.creationTime !== 'string' && !( itemAttributesToTest.creationTime instanceof Date ) && itemAttributesToTest.creationTime !== 'null' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.validatorUserId !== undefined && typeof itemAttributesToTest.validatorUserId !== 'string' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.validationTime !== undefined && typeof itemAttributesToTest.validationTime !== 'string' && !( itemAttributesToTest.validationTime instanceof Date ) && itemAttributesToTest.validationTime !== 'null' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.validFromTime !== undefined && typeof itemAttributesToTest.validFromTime !== 'string' && !( itemAttributesToTest.validFromTime instanceof Date ) && itemAttributesToTest.validFromTime !== 'null' ) {
            throw new Error();
        }
        if ( itemAttributesToTest.validToTime !== undefined && typeof itemAttributesToTest.validToTime !== 'string' && !( itemAttributesToTest.validToTime instanceof Date ) && itemAttributesToTest.validToTime !== 'null' ) {
            throw new Error();
        }

        if ( itemAttributesToTest.properties !== undefined ) {
            if ( !Array.isArray( itemAttributesToTest.properties as any ) ) {
                throw new Error();
            }

            for ( const propertyAttributes of itemAttributesToTest.properties ) {
                if ( !isPropertyAttributes( { itemId: 1, ...propertyAttributes } as any ) ) {
                    throw new Error();
                }
            }
        }

        if ( itemAttributesToTest.enumProperties !== undefined ) {
            if ( !Array.isArray( itemAttributesToTest.enumProperties as any ) ) {
                throw new Error();
            }

            for ( const enumPropertyAttributes of itemAttributesToTest.enumProperties ) {
                if ( !isEnumPropertyAttributes( { itemId: 1, ...enumPropertyAttributes } as any ) ) {
                    throw new Error();
                }
            }
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type ItemOnlyJsonAttributes = Omit<ItemAttributes, 'properties' | 'enumProperties'>
    & Required<Pick<ItemAttributes, 'id'>>;
export type ItemJsonAttributes = ItemOnlyJsonAttributes
    & { properties? : PropertyJsonAttributes[] }
    & { enumProperties? : EnumPropertyJsonAttributes[] };
export const isItemJsonAttributes = ( itemJsonAttributesToTest : any ) : itemJsonAttributesToTest is ItemJsonAttributes => {
    let retVal = false;

    try {
        if ( typeof itemJsonAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( itemJsonAttributesToTest.id === undefined || typeof itemJsonAttributesToTest.id !== 'number' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.key !== undefined && typeof itemJsonAttributesToTest.key !== 'string' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.name !== undefined && typeof itemJsonAttributesToTest.name !== 'string' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.itemTypeId === undefined || typeof itemJsonAttributesToTest.itemTypeId !== 'number' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.statusId !== undefined && typeof itemJsonAttributesToTest.statusId !== 'number' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.modifierUserId !== undefined && typeof itemJsonAttributesToTest.modifierUserId !== 'string' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.modificationTime !== undefined && typeof itemJsonAttributesToTest.modificationTime !== 'string' && !( itemJsonAttributesToTest.modificationTime instanceof Date ) && itemJsonAttributesToTest.modificationTime !== 'null' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.creatorUserId !== undefined && typeof itemJsonAttributesToTest.creatorUserId !== 'string' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.creationTime !== undefined && typeof itemJsonAttributesToTest.creationTime !== 'string' && !( itemJsonAttributesToTest.creationTime instanceof Date ) && itemJsonAttributesToTest.creationTime !== 'null' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.validatorUserId !== undefined && typeof itemJsonAttributesToTest.validatorUserId !== 'string' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.validationTime !== undefined && typeof itemJsonAttributesToTest.validationTime !== 'string' && !( itemJsonAttributesToTest.validationTime instanceof Date ) && itemJsonAttributesToTest.validationTime !== 'null' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.validFromTime !== undefined && typeof itemJsonAttributesToTest.validFromTime !== 'string' && !( itemJsonAttributesToTest.validFromTime instanceof Date ) && itemJsonAttributesToTest.validFromTime !== 'null' ) {
            throw new Error();
        }
        if ( itemJsonAttributesToTest.validToTime !== undefined && typeof itemJsonAttributesToTest.validToTime !== 'string' && !( itemJsonAttributesToTest.validToTime instanceof Date ) && itemJsonAttributesToTest.validToTime !== 'null' ) {
            throw new Error();
        }

        if ( itemJsonAttributesToTest.properties !== undefined ) {
            if ( !Array.isArray( itemJsonAttributesToTest.properties as any ) ) {
                throw new Error();
            }

            for ( const propertyJsonAttributes of itemJsonAttributesToTest.properties ) {
                if ( !isPropertyJsonAttributes( propertyJsonAttributes ) ) {
                    throw new Error();
                }
            }
        }

        if ( itemJsonAttributesToTest.enumProperties !== undefined ) {
            if ( !Array.isArray( itemJsonAttributesToTest.enumProperties as any ) ) {
                throw new Error();
            }

            for ( const enumPropertyJsonAttributes of itemJsonAttributesToTest.enumProperties ) {
                if ( !isEnumPropertyJsonAttributes( enumPropertyJsonAttributes ) ) {
                    throw new Error();
                }
            }
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};


export type ItemOnlyAttributesSettable = Partial<Pick<ItemAttributes, 'key' | 'name' | 'statusId'>>;
export const itemOnlyAttributesSettable = keys<ItemOnlyAttributesSettable>() as string[];
export type ItemAttributesSettable = ItemOnlyAttributesSettable
    & { properties? : { [ propertyTypeId : string ] : PropertyAttributesSettable } }
    & { enumProperties? : { [ propertyTypeId : string ] : EnumPropertyAttributesSettable } };
export const itemAttributesSettable = keys<ItemAttributesSettable>()
export const isItemAttributeSettable = ( attribute : string ) : attribute is keyof ItemAttributesSettable => {
    return ( itemAttributesSettable as string[] ).includes( attribute );
};

export type ItemOnlyAttributesForCreation = Required<Pick<ItemAttributes, 'itemTypeId' | 'statusId'>>
    & Partial<Pick<ItemAttributes, 'key' | 'name'>>;
export const itemOnlyAttributesForCreation = keys<ItemOnlyAttributesForCreation>()
export const isItemOnlyAttributeForCreation = ( attribute : string ) : attribute is keyof ItemOnlyAttributesForCreation => {
    return ( itemOnlyAttributesForCreation as string[] ).includes( attribute );
};
export const itemOnlyNotRequiredAttributesForCreation = keys<Pick<ItemAttributes, 'key' | 'name'>>()
export const isItemOnlyNotRequiredAttributeForCreation = ( attribute : string ) : attribute is keyof Pick<ItemAttributes, 'key' | 'name'> => {
    return ( itemOnlyNotRequiredAttributesForCreation as string[] ).includes( attribute );
};
export type ItemAttributesForCreation = ItemOnlyAttributesForCreation
    & { properties? : Omit<PropertyAttributesForCreation, 'itemId'>[] }
    & { enumProperties? : Omit<EnumPropertyAttributesForCreation, 'itemId'>[] };

export type ItemOnlyAttributesForUpdate = Required<Pick<ItemAttributes, 'id'>>
    & ItemOnlyAttributesSettable;
export type ItemAttributesForUpdate = ItemOnlyAttributesForUpdate
    & { properties? : ( PropertyAttributesForUpdate | PropertyAttributesForCreation )[] }
    & { enumProperties? : ( EnumPropertyAttributesForUpdate | EnumPropertyAttributesForCreation )[] };

export type ItemCollection = { [ itemId : number ] : Item };

/**
 * The Item class.
 */
class Item {

    /**
     * The unique Id counter for Items
     */
    static uniqueIdCount = -1;

    static getUniqueId() {
        return Item.uniqueIdCount--;
    }

    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the Item
     */
    protected _id : number;
    get id() {
        return this._id;
    }

    /**
     * The Key of the Item
     */
    protected _key : string = '';
    get key() {
        return this._key;
    }

    set key( value ) {
        if ( value !== this._key ) {
            this.validateKey( value );

            this.handleModified( 'key', value );

            this._key = value;
        }
    }

    /**
     * The Name of the Item
     */
    protected _name : string = '';
    get name() {
        return this._name;
    }

    set name( value : string ) {
        if ( value !== this._name ) {
            this.validateName( value );

            this.handleModified( 'name', value );

            this._name = value;
        }
    }

    /**
     * The TypeID of the Item
     */
    readonly itemTypeId : number;

    /**
     * The StatusID of the Item.
     */
    protected _statusId : number;
    get statusId() : number {
        return this._statusId;
    }

    set statusId( value : number ) {
        if ( value !== this._statusId ) {
            this.validateStatusId( value );

            this.handleModified( 'statusId', value );

            this._statusId = value;
        }
    }

    /**
     * The UserID of the last modifier of the Item
     */
    protected _modifierUserId : string = '';
    get modifierUserId() : string {
        return this._modifierUserId;
    }

    /**
     * The last modification date of the Item
     */
    protected _modificationTime : NDate = null;
    get modificationTime() : NDate {
        return this._modificationTime;
    }

    /**
     * The UserID of the creator of the Item
     */
    protected _creatorUserId : string = '';
    get creatorUserId() : string {
        return this._creatorUserId;
    }

    /**
     * The creation date of the Item
     */
    protected _creationTime : NDate = null;
    get creationTime() : NDate {
        return this._creationTime;
    }

    /**
     * The UserID of the validator of the Item
     */
    protected _validatorUserId : string = '';
    get validatorUserId() : string {
        return this._validatorUserId;
    }

    /**
     * The last validation date of the Item
     */
    protected _validationTime : NDate = null;
    get validationTime() : NDate {
        return this._validationTime;
    }

    /**
     * The valid from date of a History-Item
     */
    readonly validFromTime : NDate = null;

    /**
     * The valid to date of a History-Item
     */
    readonly validToTime : NDate = null;


    /*** ALIASES ***********************************************************************************************************************/

    get status() : Status {
        return metaModel[ this._schema ].getStatus( this._statusId )!;
    }

    /**
     * The ItemType of the Item
     */
    get itemType() : ItemType {
        return metaModel[ this._schema ].getItemType( this.itemTypeId )!;
    }

    /**
     * The attribute value to display the Item with
     */
    get displayAttributeValue() : string {
        return this[ this.itemType.displayAttribute ];
    }


    /*************************************************************************************************************************************/

    /**
     * The database schema this Item belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * The dataContext of the global data collection this Item is stored in
     */
    protected _dataContext = '';
    get dataContext() {
        return this._dataContext;
    }

    /**
     * An map containing the EnumProperties by PropertyTypeId.
     */
    protected _enumProperties : Map<number, EnumProperty>;
    get enumProperties() {
        return this._enumProperties;
    }

    /**
     * An map containing the Properties by PropertyTypeId.
     */
    protected _properties : Map<number, Property>;
    get properties() {
        return this._properties;
    }

    /**
     * An array containing the ReferenceIds belonging to this Item.
     */
    protected _references : Array<number>;
    get references() {
        return this._references;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof ItemOnlyAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     * The constructor.
     *
     * @param attributes                An object containing attribute-value pairs for this
     * @param [dataContext='default']   The dataContext of the global data collection this belongs to
     * @param [schema=global.schema]    The database schema this belongs to
     * @param [dontRegister=false]      Set to true to avoid registration to global data collection
     */
    constructor( attributes : ItemAttributes, dataContext : string = 'default', schema : Schema = global.schema, dontRegister : boolean = false ) {

        if ( attributes.id && attributes.id < 1 ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        if ( attributes.itemTypeId === 0 ) {
            throw new LocalizableError( 'YN.base.dataObject.item.attributes.validation.itemTypeId.isEmpty' );
        }

        if ( attributes.itemTypeId !== metaModel[ schema ].getItemType( attributes.itemTypeId )?.id ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.attributes.validation.itemTypeId.invalid',
                { itemTypeId: String( attributes.itemTypeId ), schema }
            );
        }

        this._schema = schema;

        if ( attributes.id ) {
            this._id = attributes.id;
        }
        else {
            this._id = Item.getUniqueId();
        }

        this.itemTypeId = attributes.itemTypeId;

        if ( attributes.key ) {
            this._key = attributes.key;
        }

        if ( attributes.name ) {
            this._name = attributes.name;
        }

        this._properties = new Map();
        if ( attributes.properties ) {
            try {
                this.addPropertiesFromJSON( attributes.properties );
            }
            catch ( e ) {
                if ( e instanceof StackError ) {
///////////////////////////////////////////////
                    console.warn( e.toString() );
//////////////////////////////
///////////////////////////////////////////////
/////////////////////////////////////////
//////////////////////////////
                }
            }
        }

        this._enumProperties = new Map();
        if ( attributes.enumProperties ) {
            try {
                this.addEnumPropertiesFromJSON( attributes.enumProperties );
            }
            catch ( e ) {
                if ( e instanceof StackError ) {
///////////////////////////////////////////////
                    console.warn( e.toString() );
//////////////////////////////
///////////////////////////////////////////////
/////////////////////////////////////////
//////////////////////////////
                }
            }
        }

        this._references = [];

        if ( attributes.statusId ) {
            this._statusId = attributes.statusId;
        }
        else {
            this._statusId = metaModel[ schema ].getStatus( 'active' )!.id;
        }

        if ( attributes.modifierUserId ) {
            this._modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            if ( typeof attributes.modificationTime === 'string' ) {
                this._modificationTime = convertToDate( attributes.modificationTime );
            }
            else {
                this._modificationTime = attributes.modificationTime;
            }
        }
        if ( attributes.creatorUserId ) {
            this._creatorUserId = attributes.creatorUserId;
        }
        if ( attributes.creationTime ) {
            if ( typeof attributes.creationTime === 'string' ) {
                this._creationTime = convertToDate( attributes.creationTime );
            }
            else {
                this._creationTime = attributes.creationTime;
            }
        }
        if ( attributes.validatorUserId ) {
            this._validatorUserId = attributes.validatorUserId;
        }
        if ( attributes.validationTime ) {
            if ( typeof attributes.validationTime === 'string' ) {
                this._validationTime = convertToDate( attributes.validationTime );
            }
            else {
                this._validationTime = attributes.validationTime;
            }
        }
        if ( attributes.validFromTime ) {
            if ( typeof attributes.validFromTime === 'string' ) {
                this.validFromTime = convertToDate( attributes.validFromTime );
            }
            else {
                this.validFromTime = attributes.validFromTime;
            }
        }
        if ( attributes.validToTime ) {
            if ( typeof attributes.validToTime === 'string' ) {
                this.validToTime = convertToDate( attributes.validToTime );
            }
            else {
                this.validToTime = attributes.validToTime;
            }
        }

        this._attributesModified = new Map();

        if ( !dontRegister ) {
            this._dataContext = dataContext;
            this.registerToGlobalDataCollection();
        }
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param [includeAttributes='all']   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : ItemJsonAttributes {
        // required attributes
        const retVal : ItemJsonAttributes = {
                  id        : this._id,
                  itemTypeId: this.itemTypeId
              },
              isNew                       = this.isNew();

        if ( isNew || includeAttributes !== 'modified' || this._attributesModified.has( 'key' ) ) {
            retVal.key = this._key;
        }

        if ( isNew || includeAttributes !== 'modified' || this._attributesModified.has( 'name' ) ) {
            retVal.name = this._name;
        }

        if ( isNew || includeAttributes !== 'modified' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        // not settable attributes
        if ( includeAttributes !== 'modified' ) {
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = convertValueToString( this.modificationTime, 'TIMESTAMP' );
                retVal.creatorUserId = this.creatorUserId;
                retVal.creationTime = convertValueToString( this.creationTime, 'TIMESTAMP' );
                retVal.validatorUserId = this.validatorUserId;
                retVal.validationTime = convertValueToString( this.validationTime, 'TIMESTAMP' );
            }

            // only history attributes
            else {
                retVal.validFromTime = convertValueToString( this.validFromTime, 'TIMESTAMP' );
                retVal.validToTime = convertValueToString( this.validToTime, 'TIMESTAMP' );
            }
        }

        if ( this._enumProperties.size ) {
            const enumProperties : ItemJsonAttributes[ 'enumProperties'] = [];

            for ( const enumProperty of this._enumProperties.values() ) {
                if ( enumProperty.isNew() || enumProperty.isModified() || includeAttributes !== 'modified' ) {
                    enumProperties.push( enumProperty.toJSON( includeAttributes ) );
                }
            }

            if ( enumProperties.length ) {
                retVal.enumProperties = enumProperties;
            }
        }

        if ( this._properties.size ) {
            const properties : ItemJsonAttributes[ 'properties' ] = [];

            for ( const property of this._properties.values() ) {
                if ( property.isNew() || property.isModified() || includeAttributes !== 'modified' ) {
                    properties.push( property.toJSON( includeAttributes ) );
                }
            }

            if ( properties.length ) {
                retVal.properties = properties;
            }
        }

        return retVal;
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param [includeAttributes='all']   The state of the attributes to include in result.
     */
    toUpdateJSON( includeAttributes : 'all' | 'modified' = 'all' ) : ItemAttributesSettable {
        // required attributes
        const retVal : ItemAttributesSettable = {},
              isNew                           = this.isNew();

        if ( isNew || includeAttributes !== 'modified' || this._attributesModified.has( 'key' ) ) {
            retVal.key = this._key;
        }

        if ( isNew || includeAttributes !== 'modified' || this._attributesModified.has( 'name' ) ) {
            retVal.name = this._name;
        }

        if ( isNew || includeAttributes !== 'modified' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        if ( this._enumProperties.size ) {
            const enumProperties : ItemAttributesSettable[ 'enumProperties' ] = {};

            for ( const [ propertyTypeId, enumProperty ] of this._enumProperties.entries() ) {
                if ( enumProperty.isNew() || enumProperty.isModified() || includeAttributes !== 'modified' ) {
                    enumProperties [ propertyTypeId ] = enumProperty.toUpdateJSON( includeAttributes );
                }
            }

            if ( Object.keys( enumProperties ).length ) {
                ( retVal.enumProperties as { [ propertyTypeId : number ] : EnumPropertyAttributesSettable } ) = enumProperties;
            }
        }

        if ( this._properties.size ) {
            const properties : ItemAttributesSettable[ 'properties' ] = {};

            for ( const [ propertyTypeId, property ] of this._properties.entries() ) {
                if ( property.isNew() || property.isModified() || includeAttributes !== 'modified' ) {
                    properties[ propertyTypeId ] = property.toUpdateJSON( includeAttributes );
                }
            }

            if ( Object.keys( properties ).length ) {
                retVal.properties = properties;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `Item ${this.itemType.name} '${this.displayAttributeValue}' (ID: ${this._id})`;
    }

    /**
     * Returns a string containing the XML representation of this
     * @param {boolean} [excludeDeleted=false]
     * @param {number} [indent=0]
     */
    toXML( excludeDeleted : boolean = false, indent : number = 0 ) : string {
        const itemTypeName = encodeHtmlEntities( this.itemType.name, { level: 'xml' } ),
              itemKey      = encodeHtmlEntities( this._key, { level: 'xml' } ),
              itemName     = encodeHtmlEntities( this._name, { level: 'xml' } ),
              itemStatus   = encodeHtmlEntities( this.status.name, { level: 'xml' } );

        let retVal = `<item type='${itemTypeName}' key='${itemKey}' name='${itemName}' status='${itemStatus}'`.indent( indent );

        if ( !this._properties.size && !this._enumProperties.size ) {
            retVal += ' />';
        }
        else {
            retVal += '>\r\n';

            const propertySets = this.itemType.propertySets;
            propertySets.sort( ( propertySet1, propertySet2 ) => {
                if ( propertySet1.name && propertySet2.name ) {
                    if ( propertySet1.name > propertySet2.name ) {
                        return 1;
                    }
                    else if ( propertySet1.name < propertySet2.name ) {
                        return -1;
                    }
                }

                return 0;
            } );

            for ( const propertySet of propertySets ) {
                const property = this._properties.get( propertySet.propertyTypeId )
                    ?? this._enumProperties.get( propertySet.propertyTypeId );

                if ( property ) {
                    retVal += property.toXML( excludeDeleted, indent + 1 ) + '\r\n';
                }
            }

            retVal += '</item>'.indent( indent );
        }
        return retVal;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() : Item {
        if ( this._attributesModified.has( 'key' ) ) {
            this._key = this._attributesModified.get( 'key' ) as string;
        }
        if ( this._attributesModified.has( 'name' ) ) {
            this._name = this._attributesModified.get( 'name' ) as string;
        }
        if ( this._attributesModified.has( 'statusId' ) ) {
            this._statusId = this._attributesModified.get( 'statusId' ) as number;
        }

        // reset all EnumProperties
        for ( const enumProperty of this._enumProperties.values() ) {
            enumProperty.reset();

            // remove empty enum property
            if ( !enumProperty.properties.size ) {
                this._enumProperties.delete( enumProperty.propertySet.propertyTypeId );
            }
        }

        // reset all Properties
        for ( const property of this._properties.values() ) {
            // remove new property
            if ( property.isNew() ) {
                this._properties.delete( property.propertySet.propertyTypeId );
            }
            // reset existing property
            else {
                property.reset();
            }
        }

        this._attributesModified = new Map();

        return this;
    }

    /**
     * Commits all changes of settable attributes.
     */
    commit( itemMetaDataMapping? : ItemMetaDataMapping, propertyMetaDataMapping? : PropertyMetaDataMapping ) : Item {

        // update id and metaData if new and mapped one is given
        if ( itemMetaDataMapping?.[ this._id ] ) {
            this.unregisterFromGlobalDataCollection();
            this._modifierUserId = itemMetaDataMapping[ this._id ].modifierUserId;
            this._modificationTime = convertToDate( itemMetaDataMapping[ this._id ].modificationTime );
            this._validatorUserId = itemMetaDataMapping[ this._id ].validatorUserId;
            this._validationTime = convertToDate( itemMetaDataMapping[ this._id ].validationTime );

            if ( this._key !== itemMetaDataMapping[ this._id ].key ) {
                this._key = itemMetaDataMapping[ this._id ].key;
            }

            if ( itemMetaDataMapping[ this._id ].name !== undefined && this._name !== itemMetaDataMapping[ this._id ].name ) {
                this._name = itemMetaDataMapping[ this._id ].name!;
            }

            this._creatorUserId = itemMetaDataMapping[ this._id ].creatorUserId;
            this._creationTime = convertToDate( itemMetaDataMapping[ this._id ].creationTime );

            this._id = itemMetaDataMapping[ this._id ].id;

            this.registerToGlobalDataCollection();
        }

        this._attributesModified = new Map();

        // commit all EnumProperties
        for ( const enumProperty of this._enumProperties.values() ) {
            enumProperty.commit( itemMetaDataMapping, propertyMetaDataMapping );
        }

        // commit all Properties
        for ( const property of this._properties.values() ) {
            property.commit( propertyMetaDataMapping );
        }

        return this;
    }

    /**
     * Resets the given settable attribute to its initial value.
     *
     * @param {keyof ItemOnlyAttributesSettable} attribute
     */
    resetAttribute( attribute : keyof ItemOnlyAttributesSettable ) {
        if ( this._attributesModified.has( attribute ) ) {
            switch ( attribute ) {
                case 'key':
                    this._key = this._attributesModified.get( 'key' ) as string;
                    break;

                case 'name':
                    this._name = this._attributesModified.get( 'name' ) as string;
                    break;

                case 'statusId':
                    this._statusId = this._attributesModified.get( 'statusId' ) as number;
                    break;
            }

            this._attributesModified.delete( attribute );
        }
    }

    /**
     * Deletes this by removing it from global data collection
     *
     */
    delete() {
        if ( this._dataContext !== '' ) {
            this.unregisterFromGlobalDataCollection();
        }
    }

    /**
     * Apply given attributes to this item.
     *
     * @param attributes            An object containing attribute-value pairs to be applied to this item
     * @param override              true to override attributes and existing (enum-) properties, false to merge them (means to keep modified values)
     * @param clone                 true/false to use clone/update functions of properties and enum properties
     * @param [cloneDataContext]    The dataContext the clone is registered to
     */
    update( attributes : ItemAttributesSettable = {}, override : boolean = false, clone : boolean = false, cloneDataContext? : string ) : Item {
        const deletedStatusIds = metaModel[ this._schema ].getDeletedStatus().map( s => s.id ),
              cloneOrUpdate    = clone ? 'clone' : 'update';

        if ( attributes.statusId !== undefined && attributes.statusId !== this._statusId && ( override || !this.isAttributeModified( 'statusId' ) ) ) {
            this.statusId = attributes.statusId;
        }
        if ( attributes.key !== undefined && attributes.key !== this._key && ( override || !this.isAttributeModified( 'key' ) ) ) {
            this.key = attributes.key;
        }
        if ( attributes.name !== undefined && attributes.name !== this._name && ( override || !this.isAttributeModified( 'name' ) ) ) {
            this.name = attributes.name;
        }

        // handle enumerated properties
        if ( attributes.enumProperties ) {
            for ( const enumPropertyTypeIdString of Object.keys( attributes.enumProperties ) ) {

                const enumPropertyAttributes = attributes.enumProperties[ enumPropertyTypeIdString ],
                      enumPropertyTypeId     = Number( enumPropertyTypeIdString ),
                      enumProperty           = this._enumProperties.get( enumPropertyTypeId );

                // add enumerated property
                if ( !enumProperty ) {
                    const propertySet = metaModel[ this._schema ].getPropertySetForItemTypeAndPropertyType(
                        this.itemTypeId, enumPropertyTypeId
                    );

                    this.addEnumPropertyFromJSON( {
                        itemId       : this._id,
                        propertySetId: propertySet?.id,
                        ...enumPropertyAttributes,
                        properties: undefined
                    } as EnumPropertyAttributes );

                    this._enumProperties.set( enumPropertyTypeId, this._enumProperties.get( enumPropertyTypeId )![ cloneOrUpdate ]( enumPropertyAttributes ) );

                    // remove empty enumerated property
                    if ( !this._enumProperties.get( enumPropertyTypeId )?.properties.size ) {
                        this._enumProperties.delete( enumPropertyTypeId );
                    }
                }

                // normal enumerated property update
                else if ( override || !enumProperty.isModified() ) {
                    this._enumProperties.set( enumPropertyTypeId, enumProperty[ cloneOrUpdate ]( enumPropertyAttributes ) );

                    // remove empty enumerated property
                    if ( !this._enumProperties.get( enumPropertyTypeId )?.properties.size ) {
                        this._enumProperties.delete( enumPropertyTypeId );
                    }
                }
            }
        }

        // handle properties
        if ( attributes.properties ) {
            for ( const propertyTypeIdString of Object.keys( attributes.properties ) ) {
                const propertyAttributes = attributes.properties[ propertyTypeIdString ],
                      propertyTypeId     = Number( propertyTypeIdString ),
                      property           = this._properties.get( propertyTypeId );

                // add property
                if ( !property && ( propertyAttributes[ 'statusId' ] === undefined || !deletedStatusIds.includes( propertyAttributes[ 'statusId' ] ) ) ) {
                    const propertySet = metaModel[ this._schema ].getPropertySetForItemTypeAndPropertyType( this.itemTypeId, propertyTypeId );

                    this.updatePropertyFromJSON( {
                        itemId       : this._id,
                        propertySetId: propertySet?.id,
                        ...propertyAttributes
                    } as PropertyAttributes );
                }

                else if ( property && ( override || !property.isModified() ) ) {

                    // delete property
                    if (
                        propertyAttributes[ 'statusId' ] !== undefined
                        && !deletedStatusIds.includes( property.statusId )
                        && deletedStatusIds.includes( propertyAttributes[ 'statusId' ] )
                    ) {
                        // remove new property
                        if ( property.isNew() ) {
                            this._properties.delete( propertyTypeId );
                        }

                        // delete existing property
                        else {
                            this._properties.set( propertyTypeId, property.reset()[ cloneOrUpdate ]( { statusId: propertyAttributes[ 'statusId' ] } ) );
                        }
                    }

                    // normal property update
                    else {
                        this._properties.set( propertyTypeId, property[ cloneOrUpdate ]( propertyAttributes ) );
                    }
                }
            }
        }

        if ( clone && cloneDataContext ) {
            this._dataContext = cloneDataContext;
        }

        return this;
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param attributes            An object containing attribute-value pairs to be applied to the clone
     * @param override              true to override attributes and existing (enum-) properties, false to merge them (means to keep modified values)
     * @param [cloneDataContext]    The dataContext the clone is registered to
     */
    clone( attributes : ItemAttributesSettable = {}, override : boolean = false, cloneDataContext? : string ) : Item {
        // unregister if
        if ( this._dataContext !== '' && !cloneDataContext ) {
            this.unregisterFromGlobalDataCollection();
        }

        const clone : Item = omniclone(
            this,
            {
                invokeConstructors: false,
                setPrototype      : true,
                copyGettersSetters: true
            },
            ( node : any ) => {
                // do not clone (enum)properties - it's done in update
                if ( node instanceof Property || node instanceof EnumProperty ) {
                    return node;
                }
                else {
                    return undefined;
                }
            }
        );

        // clone and update parent in (enum)properties
        for ( const propertyTypeId of clone.enumProperties.keys() ) {
            const enumProperty = clone.enumProperties.get( propertyTypeId )!.clone();

            enumProperty.parent = clone;

            clone.enumProperties.set( propertyTypeId, enumProperty );
        }
        for ( const propertyTypeId of clone.properties.keys() ) {
            const property = clone.properties.get( propertyTypeId )!.clone();

            property.parent = clone;

            clone.properties.set( propertyTypeId, property );
        }

        // apply data
        try {
            clone.update( attributes, override, true, cloneDataContext );

            // register cloned item if
            if ( clone.dataContext !== '' ) {
                clone.registerToGlobalDataCollection();
            }

            return clone;
        }
        catch ( e ) {
            // re-register original item if
            if ( this._dataContext !== '' && !cloneDataContext ) {
                this.registerToGlobalDataCollection();
            }

            throw ( e );
        }
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this._id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this.validFromTime !== null;
    }

    /**
     * Checks if this is deleted.
     */
    isDeleted() : boolean {
        return metaModel[ this._schema ].getDeletedStatus().map( s => s.id ).includes( this._statusId );
    }

    /**
     * Checks if this or an EnumProperty or a Property of this is modified.
     */
    isModified() : boolean {
        let retVal = this._attributesModified.size > 0;

        // check if an EnumProperty is modified
        if ( !retVal && this._enumProperties.size ) {
            for ( const enumProperty of this._enumProperties.values() ) {
                if ( enumProperty.isModified() || enumProperty.isNew() ) {
                    retVal = true;
                    break;
                }
            }
        }

        // check if a Property is modified
        if ( !retVal && this._properties.size ) {
            for ( const property of this._properties.values() ) {
                if ( property.isModified() || property.isNew() ) {
                    retVal = true;
                    break;
                }
            }
        }

        return retVal;
    }

    /**
     * Checks if this and all EnumProperties and all Properties of this are valid.
     */
    isValid() : boolean {
        let retVal = true;

        // new
        if ( this.isNew() ) {
            // key is autogenerated and key set
            if ( this.itemType.isKeyAutogenerated && this._key !== '' ) {
                retVal = false;
            }
            // key is not autogenerated and no key set
            else if ( !this.itemType.isKeyAutogenerated && this._key === '' ) {
                retVal = false;
            }

            // no name set
            if ( this._name === '' ) {
                retVal = false;
            }

            // deleted
            if ( this.isDeleted() ) {
                retVal = false;
            }
        }

        // modified
        else {
            // key is autogenerated and key modified
            if ( this.itemType.isKeyAutogenerated && this.isAttributeModified( 'key' ) ) {
                retVal = false;
            }
            // key is modified and empty
            else if ( this.isAttributeModified( 'key' ) && this._key === '' ) {
                retVal = false;
            }

            // name is modified and empty
            if ( this.isAttributeModified( 'name' ) && this._name === '' ) {
                retVal = false;
            }

            // deleted and modified
            if ( this.isDeleted() && ( this.isAttributeModified( 'key' ) || this.isAttributeModified( 'name' ) ) ) {
                retVal = false;
            }
        }

        // check if EnumProperties are valid
        if ( retVal && this._enumProperties.size ) {
            for ( const enumProperty of this._enumProperties.values() ) {
                if ( !enumProperty.isValid() ) {
                    retVal = false;
                    break;
                }
            }
        }

        // check if Properties are valid
        if ( retVal && this._properties.size ) {
            for ( const property of this._properties.values() ) {
                if ( !property.isValid() ) {
                    retVal = false;
                    break;
                }
            }
        }

        return retVal;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof ItemOnlyAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    /**
     * Gets if the (original) unmodified value for given attribute is modified .
     *
     * @param attribute        The attribute to get value for
     */
    getAttributeOriginalValue( attribute : keyof ItemOnlyAttributesSettable ) : AttributeValue {
        return this._attributesModified.get( attribute ) ?? this[ attribute ];
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof ItemOnlyAttributesSettable, value : AttributeValue ) {
        // set as modified
        if ( !this._attributesModified.has( attribute ) && value !== this[ attribute ] ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && value === this._attributesModified.get( attribute ) ) {
            this._attributesModified.delete( attribute );
        }
    }

    protected checkStatus() {
        if ( this.isDeleted() ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.attributes.validation.couldNotChangeDeleted'
            );
        }
    }

    /**
     * Validates the given statusId
     */
    protected validateStatusId( statusId : number ) {
        if ( !metaModel[ this._schema ].getStatus( statusId ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.statusId.notExists' );
        }

        if (
            (
                statusId === metaModel[ this._schema ].getStatus( 'history' )?.id
                || statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id
            )
            && (
                this._attributesModified.size > 1
                || ( this._attributesModified.size === 1 && !this._attributesModified.has( 'statusId' ) )
            )
        ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.attributes.validation.couldNotDeleteChanged'
            );
        }
    }

    /**
     * Validates the key for this
     */
    protected validateKey( key : string ) {
        this.checkStatus();

        // autogenerated
        if ( this.itemType.isKeyAutogenerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.attributes.validation.key.autogenerated',
                { itemType: this.itemType.name, schema: this._schema }
            );
        }

        // empty
        if ( key === '' ) {
            throw new LocalizableError( 'YN.base.dataObject.item.attributes.validation.key.isEmpty' );
        }

        // white spaces
        if ( / /.test( key ) ) {
            throw new LocalizableError( 'YN.base.dataObject.item.attributes.validation.key.containsWhiteSpaces' );
        }

        // length
        if ( key.length > 255 ) {
            throw new LocalizableError( 'YN.base.dataObject.item.attributes.validation.key.tooLong' );
        }

        // TODO: implement key definition validation
    }

    /**
     * Validates the name for this
     */
    protected validateName( name : string ) {
        this.checkStatus();

        // length
        if ( name.length > 255 ) {
            throw new LocalizableError( 'YN.base.dataObject.item.attributes.validation.name.tooLong' );
        }

        // TODO: implement name definition validation
    }

    /**
     * Registers this to the global data collection.
     */
    protected registerToGlobalDataCollection() {
        if ( !global.data[ this._dataContext ] ) {
            global.data[ this._dataContext ] = {
                items     : {},
                references: {},
                schema    : this._schema
            };
        }

        global.data[ this._dataContext ].items[ this._id ] = this;
    }

    /**
     * Un-registers this from the global data collection.
     */
    protected unregisterFromGlobalDataCollection() {
        if ( global.data[ this._dataContext ]?.items[ this._id ] ) {
            delete global.data[ this._dataContext ].items[ this._id ];
        }
    }


    /*** PROPERTIES RELATED **************************************************************************************************************/

    /**
     * Adds Properties given by data to this.
     *
     * @param dataObject            An array of propertyData
     */
    protected addPropertiesFromJSON( dataObject : ( Omit<PropertyAttributes, 'itemId'> & Partial<Pick<PropertyAttributes, 'itemId'>> )[] ) {
        const stackError = new StackError(
            'YN.base.dataObject.item.addPropertiesFromJSON.error.title',
            { item: this.toString() }
        );

        for ( const propertyData of dataObject ) {
            try {
                this.addPropertyFromJSON( propertyData );
            }
            catch ( addError ) {
                const propertySet   = metaModel[ this._schema ].getPropertySet( propertyData.propertySetId ),
                      subStackError = new StackError(
                          'YN.base.dataObject.item.addProperty.error.title',
                          { propertyTypeName: propertySet?.name ?? 'n/a' }
                      );

                stackError.add( subStackError );
                subStackError.add( addError );
            }
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError );
        }
    }

    /**
     * Adds or updates Properties given by data to this.
     *
     * @param dataObject An array of propertyData
     */
    protected addPropertyFromJSON( dataObject : Omit<PropertyAttributes, 'itemId'> & Partial<Pick<PropertyAttributes, 'itemId'>> ) : Property {
        return this.addOrUpdatePropertyFromJSON( dataObject, false );
    }

    /**
     * Adds or updates Properties given by data to this.
     *
     * @param dataObject An array of propertyData
     */
    protected updatePropertyFromJSON( dataObject : Omit<PropertyAttributes, 'itemId'> & Partial<Pick<PropertyAttributes, 'itemId'>> ) : Property {
        return this.addOrUpdatePropertyFromJSON( dataObject, true );
    }

    /**
     * Adds a Property given by data to this.
     *
     * @param dataObject   An object containing attribute-value pairs
     * @param allowUpdates true to allow updates, if false
     */
    private addOrUpdatePropertyFromJSON( dataObject : Omit<PropertyAttributes, 'itemId'> & Partial<Pick<PropertyAttributes, 'itemId'>>, allowUpdates : boolean = false ) : Property {
        let retVal : Property;
        const propertySet = metaModel[ this._schema ].getPropertySet( dataObject.propertySetId );

        if ( !propertySet || propertySet.itemTypeId !== this.itemTypeId ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.noPSet',
                { propertyTypeName: propertySet?.name ?? `PropertySetId: ${dataObject.propertySetId}`, itemTypeName: this.itemType.name }
            );
        }

        if ( !allowUpdates && this._properties.has( propertySet.propertyTypeId ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.exists',
                { propertyTypeName: propertySet.name, itemId: String( this._id ) }
            );
        }

        if ( propertySet.isEnumerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.isEnumerated',
                { propertyTypeName: propertySet.name, itemTypeName: this.itemType.name }
            );
        }

        if ( this._properties.has( propertySet.propertyTypeId ) ) {
            retVal = this._properties.get( propertySet.propertyTypeId )!.update( dataObject );
        }
        else {
            retVal = new Property( this, { ...dataObject, itemId: this._id } );
            this._properties.set( propertySet.propertyTypeId, retVal );
        }

        return retVal;
    }

    /**
     * Adds a new Property by PropertyType to this.
     *
     * @param propertyTypeId
     */
    addProperty( propertyTypeId : number ) : Property {
        if ( this._properties.has( propertyTypeId ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.exists',
                { propertyTypeName: this._properties.get( propertyTypeId )!.propertySet.name, itemId: String( this._id ) }
            );
        }

        const propertySet = metaModel[ this._schema ].getPropertySetForItemTypeAndPropertyType( this.itemTypeId, propertyTypeId );

        if ( !propertySet ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.noPSet',
                { propertyTypeName: metaModel[ this._schema ].getPropertyType( propertyTypeId )?.name ?? `PropertyTypeId: ${propertyTypeId}`, itemTypeName: this.itemType.name }
            );
        }

        if ( propertySet.isEnumerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.isEnumerated',
                { propertyTypeName: propertySet.name, itemTypeName: this.itemType.name }
            );
        }

        const newProperty = new Property( this, { itemId: this._id, propertySetId: propertySet.id, statusId: this._statusId } );

        this._properties.set( propertyTypeId, newProperty );

        return newProperty;
    }


    /*** ENUM PROPERTIES RELATED *********************************************************************************************************/

    /**
     * Adds EnumProperties given by data to this.
     *
     * @param dataObject An array of propertyData
     */
    protected addEnumPropertiesFromJSON( dataObject : ( Omit<EnumPropertyAttributes, 'itemId'> & Partial<Pick<EnumPropertyAttributes, 'itemId'>> )[] ) {
        const stackError = new StackError(
            'YN.base.dataObject.item.addEnumPropertiesFromJSON.error.title',
            { item: this.toString() }
        );

        for ( const propertyData of dataObject ) {
            try {
                this.addEnumPropertyFromJSON( propertyData );
            }
            catch ( addError ) {
                const propertySet   = metaModel[ this._schema ].getPropertySet( propertyData.propertySetId ),
                      subStackError = new StackError(
                          'YN.base.dataObject.item.addEnumProperty.error.title',
                          { propertyTypeName: propertySet?.name ?? 'n/a' }
                      );

                stackError.add( subStackError );
                subStackError.add( addError );
            }
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError );
        }
    }

    /**
     * Adds a EnumProperty given by data to this.
     *
     * @param dataObject An object containing attribute-value pairs
     */
    protected addEnumPropertyFromJSON( dataObject : Omit<EnumPropertyAttributes, 'itemId'> & Partial<Pick<EnumPropertyAttributes, 'itemId'>> ) : EnumProperty {
        return this.addOrUpdateEnumPropertyFromJSON( dataObject, false );
    }

    /**
     * Updates a EnumProperty given by data to this.
     *
     * @param dataObject An object containing attribute-value pairs
     */
    protected updateEnumPropertyFromJSON( dataObject : Omit<EnumPropertyAttributes, 'itemId'> & Partial<Pick<EnumPropertyAttributes, 'itemId'>> ) : EnumProperty {
        return this.addOrUpdateEnumPropertyFromJSON( dataObject, true );
    }

    /**
     * Adds or updates a EnumProperty given by data to this.
     *
     * @param dataObject   An object containing attribute-value pairs
     * @param allowUpdates true to allow updates, if false
     */
    private addOrUpdateEnumPropertyFromJSON( dataObject : Omit<EnumPropertyAttributes, 'itemId'> & Partial<Pick<EnumPropertyAttributes, 'itemId'>>, allowUpdates : boolean = false ) : EnumProperty {
        let retVal : EnumProperty;
        const propertySet = metaModel[ this._schema ].getPropertySet( dataObject.propertySetId );

        if ( !propertySet || propertySet.itemTypeId !== this.itemTypeId ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.noPSet',
                { propertyTypeName: propertySet?.name ?? `PropertySetId: ${dataObject.propertySetId}`, itemTypeName: this.itemType.name }
            );
        }

        if ( !allowUpdates && this._enumProperties.has( propertySet.propertyTypeId ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.exists',
                { propertyTypeName: propertySet.name, itemId: String( this._id ) }
            );
        }

        if ( !propertySet.isEnumerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.isNotEnumerated',
                { propertyTypeName: propertySet.name, itemTypeName: this.itemType.name }
            );
        }

        if ( this._properties.has( propertySet.propertyTypeId ) ) {
            retVal = this._enumProperties.get( propertySet.propertyTypeId )!.update( dataObject as EnumPropertyAttributesSettable );
        }
        else {
            retVal = new EnumProperty( this, { ...dataObject, itemId: this._id } );
            this._enumProperties.set( propertySet.propertyTypeId, retVal );
        }

        return retVal;
    }

    /**
     * Adds a new EnumProperty by PropertyType to this.
     *
     * @param propertyTypeId
     */
    addEnumProperty( propertyTypeId : number ) : EnumProperty {
        if ( this._enumProperties.has( propertyTypeId ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.exists',
                { propertyType: this._enumProperties.get( propertyTypeId )!.propertySet.name, itemId: String( this._id ) }
            );
        }

        const propertySet = metaModel[ this._schema ].getPropertySetForItemTypeAndPropertyType( this.itemTypeId, propertyTypeId );

        if ( !propertySet ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.noPSet',
                { propertyTypeName: metaModel[ this._schema ].getPropertyType( propertyTypeId )?.name ?? `PropertyTypeId: ${propertyTypeId}`, itemTypeName: this.itemType.name }
            );
        }

        if ( !propertySet.isEnumerated ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.addProperty.isNotEnumerated',
                { propertyTypeName: propertySet.name, itemTypeName: this.itemType.name }
            );
        }

        const newEnumProperty = new EnumProperty( this, { itemId: this._id, propertySetId: propertySet.id } );

        this._enumProperties.set( propertyTypeId, newEnumProperty );

        return newEnumProperty;
    }


    /*** REFERENCES RELATED **************************************************************************************************************/

    /**
     * Adds the given reference to the _references array if the reference belonging to this.
     *
     * @param reference         The reference to register
     */
    registerReference( reference : Reference ) {
        if ( !this._references.includes( reference.id ) ) {
            if ( reference.srcItemId === this._id || reference.destItemId === this._id ) {
                this._references.push( reference.id );
            }
        }
    }

    /**
     * Removes the given reference to the _references array if the reference belonging to this.
     *
     * @param reference         The reference to unregister
     */
    unregisterReference( reference : Reference | number ) {
        const refIdx = this._references.indexOf( _.isNumber( reference ) ? reference : reference.id );
        if ( refIdx !== -1 ) {
            this._references.splice( refIdx, 1 );
        }
    }

}

export default Item;
